<script setup lang="ts">
import type { NuxtError } from "#app";
import { useTranslation } from "~/composables/useTranslation";
const TsHeaderCheckout = defineAsyncComponent(
  () => import("~/components/TsHeaderCheckout.vue")
);

const config = useRuntimeConfig();

const { isMobile } = useDevice();

const { $customClientErrorHandler } = useNuxtApp();

const props = defineProps({
  error: Object as () => NuxtError,
});

const handleError = () => clearError({ redirect: "/" });


// GTM Logger On Nuxt Error Boundary: EW-479
function logErrorBoundary(cause: "PageNotFound" | "SomethingWentWrong", error?: NuxtError) {
  logEvent({
    options: {
      eventAction: "error",
      eventProperties: error,
      eventInteraction: true,
      eventLabel: `Error Page - ${error?.statusCode} ${cause}`,
      eventName: "error_messages",
    },
  });
}

onMounted(async() => {
  if(props.error && props.error.statusCode === 404){
    let errorMeta = {pageNotFoundError: true}


    $customClientErrorHandler(
        props.error,
        'high',
        'Error Page - 404 Page Not Found',
        errorMeta
    );
    logErrorBoundary("PageNotFound", props.error);
  } else {
    let errorMeta = { serverError: true };

    $customClientErrorHandler(
        props.error,
        'high',
        'Something went wrong',
        errorMeta
    );
    logErrorBoundary("SomethingWentWrong", props.error);
  }
});
</script>

<template>
  <div class="h-screen">
    <TsHeaderCheckout />
    <TsSection
      appendClass="h-full grid place-content-center mb-0 px-0 bg-cover bg-no-repeat pb-7 pt-1.5"
    >
      <TsTypography
        align="center"
        variant="h1"
        appendClass="text-[#065CAB] mb-0"
        >Oops!</TsTypography
      >

      <TsImage
        v-if="error && error.statusCode === 404"
        src="/images/error404.png"
        alt=""
        height="156"
        width="273"
        class="mx-auto object-cover"
        loading="lazy"
      />

      <TsImage
        v-else
        src="/images/500Error.png"
        alt="bannerImage"
        height="156"
        width="337"
        class="mx-auto object-cover"
        loading="lazy"
      />

      <TsTypography align="center" variant="h4" appendClass="mb-5">
        {{
          config.public.appDebug
            ? error?.message
            : useTranslation("errorMessage1", "Er is iets misgegaan.")
        }}
      </TsTypography>

      <TsTypography
        align="center"
        :appendClass="isMobile ? 'mb-5 w-full' : 'mb-5 w-1/3 mx-auto'"
      >
        {{
          useTranslation(
            "errorMessage2",
            "Onze excuses voor de hik. Ons team van technische tovenaars is gestuurd om de gremlins in het systeem te temmen"
          )
        }}
      </TsTypography>

      <TsMedia appendClass="justify-center">
        <TsButton
          @click="handleError"
          size="md"
          :label="useTranslation('continueShopping', 'Ga door met winkelen')"
          appendClass="whitespace-nowrap px-9"
        />
      </TsMedia>
    </TsSection>
  </div>
</template>
